import React from 'react';
import type { Components } from 'react-markdown';
import type { ChatMarkdownVariant } from './types';

/**
 * Factory that builds a `react-markdown` component map tuned to a color
 * palette. Callers pick the variant (neutral for white assistant bubbles,
 * teal for system cards, etc.) and get consistent typography + table styling.
 */
export const createChatMarkdownComponents = (
  variant: ChatMarkdownVariant,
): Components => ({
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }) => (
    <ul className="list-disc pl-4 my-2 space-y-1 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-4 my-2 space-y-1 last:mb-0">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  code: ({ children }) => (
    <code className="bg-neutral-100 rounded px-1 py-0.5 text-xs font-mono">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="bg-neutral-100 rounded-lg p-3 my-2 text-xs font-mono overflow-x-auto">
      {children}
    </pre>
  ),
  h1: ({ children }) => (
    <h1 className="text-base font-semibold mb-1">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-semibold mb-1">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-medium mb-1">{children}</h3>
  ),
  blockquote: ({ children }) => (
    <blockquote className={`border-l-2 ${variant.rule} pl-3 my-2 italic`}>
      {children}
    </blockquote>
  ),
  hr: () => <hr className={`${variant.rule} my-2`} />,
  a: ({ href, children }) => (
    <a
      href={href}
      className={`${variant.link} underline`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div
      className={`my-2 scrollbar-visible overflow-x-auto rounded-xl border ${variant.tableBorder}`}
      data-testid="chat-markdown-table"
    >
      <table className="w-max min-w-full text-xs border-collapse">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className={`border-b ${variant.tableHead}`}>{children}</thead>
  ),
  tbody: ({ children }) => (
    <tbody className={`divide-y ${variant.tableDivide}`}>{children}</tbody>
  ),
  tr: ({ children }) => (
    <tr className={`${variant.tableRowHover} transition-colors`}>{children}</tr>
  ),
  th: ({ children }) => (
    <th
      className={`px-3 py-2 text-left font-semibold uppercase tracking-wide whitespace-nowrap ${variant.tableHeadText}`}
    >
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className={`px-3 py-2 align-top ${variant.tableCellText}`}>
      {children}
    </td>
  ),
});

/** Assistant and streaming bubbles — white card on a neutral thread. */
export const chatMarkdownComponents = createChatMarkdownComponents({
  link: 'text-blue-600',
  tableBorder: 'border-neutral-200',
  tableHead: 'bg-neutral-50 border-neutral-200',
  tableHeadText: 'text-neutral-500',
  tableDivide: 'divide-neutral-100',
  tableRowHover: 'hover:bg-neutral-50',
  tableCellText: 'text-neutral-800',
  rule: 'border-neutral-200',
});

/** Teal system card — keeps table chrome in the card's palette. */
export const systemChatMarkdownComponents = createChatMarkdownComponents({
  link: 'text-teal-700',
  tableBorder: 'border-teal-200',
  tableHead: 'bg-teal-100/50 border-teal-200',
  tableHeadText: 'text-teal-700',
  tableDivide: 'divide-teal-100',
  tableRowHover: 'hover:bg-teal-100/40',
  tableCellText: 'text-teal-900',
  rule: 'border-teal-200',
});
